import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import CytoscapeComponent from "react-cytoscapejs";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORY_COLORS, CATEGORY_LABELS, categoryColor, prettyType } from "@/lib/intel";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NetworkBrief } from "@/components/NetworkBrief";
import { toast } from "sonner";
import { Crosshair, Maximize2, X, Sparkles } from "lucide-react";

const STYLESHEET = [
  {
    selector: "node",
    style: {
      "background-color": "data(color)",
      label: "data(label)",
      color: "#0f172a",
      "font-size": "9px",
      "font-family": "monospace",
      "text-wrap": "wrap",
      "text-max-width": "90px",
      "text-valign": "bottom",
      "text-margin-y": 3,
      width: 22, height: 22,
      "border-width": 2,
      "border-color": "#ffffff",
    },
  },
  { selector: "node[?synthetic]", style: { "border-color": "#94a3b8", "border-style": "dashed" } },
  { selector: "node:selected", style: { "border-color": "#0f172a", "border-width": 3, width: 30, height: 30 } },
  {
    selector: "edge",
    style: {
      width: 1.2,
      "line-color": "#cbd5e1",
      "target-arrow-color": "#cbd5e1",
      "target-arrow-shape": "triangle",
      "arrow-scale": 0.7,
      "curve-style": "bezier",
      label: "data(label)",
      "font-size": "7px",
      "font-family": "monospace",
      color: "#94a3b8",
      "text-rotation": "autorotate",
    },
  },
  { selector: "edge:selected", style: { "line-color": "#2563eb", "target-arrow-color": "#2563eb", width: 2 } },
];

export default function IntelGraph() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const focus = params.get("focus") || "";
  const cyRef = useRef(null);
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [networks, setNetworks] = useState([]);
  const [networkFilter, setNetworkFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchGraph = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: stats }] = await Promise.all([
        api.get("/intel/graph", { params: focus ? { focus_id: focus, depth: 2 } : {} }),
        api.get("/intel/stats"),
      ]);
      setGraph(data);
      setNetworks(stats.networks || []);
    } finally {
      setLoading(false);
    }
  }, [focus]);

  useEffect(() => { fetchGraph(); }, [fetchGraph]);

  const elements = useMemo(() => {
    const visibleNodes = graph.nodes.filter((n) => !networkFilter || n.network === networkFilter);
    const ids = new Set(visibleNodes.map((n) => n.id));
    const nodeEls = visibleNodes.map((n) => ({
      data: { id: n.id, label: n.label.replace("[DEMO] ", ""), color: categoryColor(n.category), category: n.category, type: n.type, network: n.network, synthetic: n.synthetic },
    }));
    const edgeEls = graph.edges
      .filter((e) => ids.has(e.source) && ids.has(e.target))
      .map((e) => ({ data: { id: e.id, source: e.source, target: e.target, label: prettyType(e.type) } }));
    return [...nodeEls, ...edgeEls];
  }, [graph, networkFilter]);

  const runLayout = useCallback(() => {
    if (cyRef.current) {
      cyRef.current.layout({ name: "cose", animate: false, padding: 30, nodeRepulsion: 8000, idealEdgeLength: 90 }).run();
      cyRef.current.fit(undefined, 40);
    }
  }, []);

  useEffect(() => {
    if (cyRef.current && elements.length) {
      const t = setTimeout(runLayout, 60);
      return () => clearTimeout(t);
    }
  }, [elements, runLayout]);

  const onCy = (cy) => {
    cyRef.current = cy;
    cy.removeAllListeners();
    cy.on("tap", "node", (evt) => {
      const d = evt.target.data();
      setSelected(d);
    });
    cy.on("tap", (evt) => { if (evt.target === cy) setSelected(null); });
  };

  const usedCategories = useMemo(() => Array.from(new Set(graph.nodes.map((n) => n.category))), [graph]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="intel-graph-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700">Intelligence graph</div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Network explorer</h1>
          <p className="text-sm text-slate-500 mt-1">{focus ? "Neighbourhood view — click any node to pivot." : "Full graph — click a node to inspect it."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="network-filter" value={networkFilter} onChange={(e) => setNetworkFilter(e.target.value)} className="border border-slate-300 rounded-md text-sm px-2 py-2">
            <option value="">All networks</option>
            {networks.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {networkFilter && (
            <Button data-testid="explain-network-btn" className="bg-purple-600 hover:bg-purple-700 gap-1.5" onClick={() => navigate(`/intel/networks/${encodeURIComponent(networkFilter)}`)}>
              <Sparkles className="h-4 w-4" /> Explain this network
            </Button>
          )}
          {focus && <Button data-testid="view-full-graph" variant="outline" className="border-slate-300 gap-1.5" onClick={() => navigate("/intel/graph")}><Maximize2 className="h-4 w-4" /> Full graph</Button>}
          <Button data-testid="refit-graph" variant="outline" className="border-slate-300 gap-1.5" onClick={runLayout}><Crosshair className="h-4 w-4" /> Re-fit</Button>
        </div>
      </header>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-testid="graph-legend">
        {usedCategories.map((cat) => (
          <span key={cat} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] || CATEGORY_COLORS.other }} />
            {CATEGORY_LABELS[cat] || cat}
          </span>
        ))}
      </div>

      <div className="relative border border-slate-200 bg-white rounded-md overflow-hidden" style={{ height: "calc(100vh - 260px)", minHeight: 420 }}>
        {loading ? (
          <div className="absolute inset-0 grid place-items-center text-sm text-slate-500" data-testid="graph-loading">Building graph…</div>
        ) : elements.length === 0 ? (
          <div className="absolute inset-0 grid place-items-center text-sm text-slate-400">No entities to display.</div>
        ) : (
          <CytoscapeComponent
            elements={elements}
            stylesheet={STYLESHEET}
            style={{ width: "100%", height: "100%" }}
            cy={onCy}
            minZoom={0.2}
            maxZoom={2.5}
          />
        )}

        {selected && (
          <div data-testid="node-panel" className="absolute top-3 right-3 w-72 bg-white border border-slate-200 rounded-md shadow-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: categoryColor(selected.category) }} />
                  <span className="text-[11px] font-mono uppercase text-slate-500">{prettyType(selected.type)}</span>
                </div>
                <div className="font-display font-bold tracking-tight text-sm mt-1">{selected.label}</div>
                {selected.network && <div className="text-[11px] text-slate-500 font-mono">{selected.network}</div>}
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex gap-2">
              <Link to={`/intel/entities/${selected.id}`} data-testid="node-open-profile" className="flex-1 text-center text-xs bg-slate-900 hover:bg-slate-800 text-white px-2 py-1.5 rounded-md">Open profile</Link>
              <Link to={`/intel/graph?focus=${selected.id}`} data-testid="node-pivot" className="flex-1 text-center text-xs border border-slate-300 hover:bg-slate-50 px-2 py-1.5 rounded-md">Pivot here</Link>
            </div>
            {selected.network && (
              <Link to={`/intel/networks/${encodeURIComponent(selected.network)}`} data-testid="node-explain-network"
                className="flex items-center justify-center gap-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1.5 rounded-md">
                <Sparkles className="h-3.5 w-3.5" /> Explain this network
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
